import { BaseResource } from '@/models/_base.model';
import { CarePlanFlat } from '@/models/care-plan.model';
import { CouponFlat } from '@/models/coupon.model';
import { InvoiceFlat, InvoiceStatusEnum } from '@/models/invoice.model';
import {
  MedicationInstructionsFrequency,
  MedicationInstructionsQuantity
} from '@/models/medication-instructions.model';
import { OrderStatus } from '@/models/order.model';
import { PatientFlat } from '@/models/patient.model';
import { Product } from '@/models/product.model';
import { ShippingMethod } from '@/models/shipping-method.model';
import { PharmacyEnum } from '@/models/common.model';
import { UserFlatRelationship } from '@/models/user.model';
import { Interval } from '@/types/common.type';

export enum SubscriptionStatusEnum {
  AWAITING_ACTIVATION = 'awaiting_activation',
  SCHEDULED = 'scheduled',
  INCOMPLETE = 'incomplete',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  CANCELED = 'canceled'
}

export enum SubscriptionFulfillmentStatusEnum {
  IDLE = 'idle',
  IN_PROGRESS = 'in_progress',
  FULFILLED = 'fulfilled',
  CANCELED = 'canceled'
}

export enum PaymentRequestNotAllowedReasonEnum {
  MISSING_INITIAL_PAYMENT = 'missing_initial_payment',
  AUTO_RENEWAL = 'auto_renewal',
  ALREADY_REQUESTED = 'already_requested'
}

export type SubscriptionItem = {
  id: string;
  subscriptionId: string;
  product: Product;
  qty: number;
  unitAmount: number;
  instructionsQuantity: MedicationInstructionsQuantity | null;
  instructionsFrequency: MedicationInstructionsFrequency | null;
  cancellationReason: string | null;
  lastPaymentAt: string | null;
  canceledAt: string | null;
  instructionsText: string | null;
};

export type SubscriptionFlat = BaseResource & {
  accountId: string;
  patientId: string;
  latestInvoiceId: string | null;
  version: number;
  status: SubscriptionStatusEnum;
  isAuto: boolean;
  isNextPaymentScheduled: boolean;
  billingInterval: Interval;
  carePlanUnitAmount: number;
  carePlanQty: number;
  carePlanAmount: number;
  shippingAmount: number;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  nextPaymentAt: string | null;
  lastPaymentAt: string | null;
  startedAt: string | null;
  startsAt: string | null;
  canceledAt: string | null;
  cancelsAt: string | null;
  requestedPaymentAt: string | null;
  earlyRenewalReason: string | null;
  isCarePlanReadyForCharge: boolean;
  carePlan: CarePlanFlat | null;
  items: SubscriptionItem[];
  cancellationReason: string | null;
  paymentRequestNotAllowedReason: PaymentRequestNotAllowedReasonEnum | null;
  latestInvoiceStatus: InvoiceStatusEnum | null;
  latestFulfillmentStatus: SubscriptionFulfillmentStatusEnum | null;
};

export type Subscription = SubscriptionFlat & {
  patient: PatientFlat;
  author: UserFlatRelationship;
};

export type SubscriptionFull = Subscription & {
  shippingMethod: ShippingMethod;
  latestInvoice: InvoiceFlat | null;
  nextInvoiceCoupon: CouponFlat | null;
  latestInvoiceOrders: OrderStatus[];
};

export type CreateSubscriptionDto =
  | {
      accountId: string;
      patientId: string;
      carePlanId: string;
      shippingMethodId: string;
      couponId: string | null;
      isAuto: boolean;
      items: CreateSubscriptionItemDto[];
      billingIntervalMonths: number;
    }
  | {
      status: SubscriptionStatusEnum.SCHEDULED;
      startsAt: string;
    }
  | {
      status: SubscriptionStatusEnum.AWAITING_ACTIVATION | null;
      startsAt?: never;
    };

export type BulkCreateSubscriptionDto = CreateSubscriptionDto[];

export type UpdateSubscriptionDto = Partial<{
  isAuto: boolean;
  isNextPaymentScheduled: boolean;
  billingIntervalMonths: number;
  startsAt: string;
  nextPaymentAt: string;
  requestedPaymentAt: string | null;
  earlyRenewalReason: string | null;
  cancelsAt: string | null;
  cancellationReason: string;
  items: (CreateSubscriptionItemDto | UpdateSubscriptionItemDto)[];
  shippingMethodId: string;
  nextInvoiceCouponId: string | null;
}>;

export type AdvanceSubscriptionDto = {
  items?: (CreateSubscriptionItemDto | UpdateSubscriptionItemDto)[];
  couponId?: string | null;
  shippingMethodId?: string;
};

export type CreateSubscriptionItemDto = {
  productId: string;
  qty: number;
  unitAmount?: number | null;
  instructionsQuantityId: string;
  instructionsFrequencyId: string;
  prescriptionId?: string;
  prescription?: {
    prescribedItem?: string | null;
    sigText: string;
    pharmacy: PharmacyEnum | null;
    supplyDays: number | null;
    prescriberId: string | null;
    rxNumber: string;
    writtenQuantity: number;
    writtenAt: string;
    refillsWritten?: number | null;
  };
};

export type UpdateSubscriptionItemDto = Partial<
  Omit<
    CreateSubscriptionItemDto,
    'instructionsFrequencyId' | 'instructionsQuantityId'
  >
> & {
  id: string;
  isCanceled?: boolean;
  cancellationReason?: string | null;
};
